import { addMonths, differenceInMonths, endOfMonth, isAfter, isBefore } from 'date-fns';
import { formatDate } from '@common/paymentsUtils/dateUtils';

interface NextDueDateInterface {
  firstDueDate: string;
  today?: Date;
  delay?: number;
  monthlyPaymentDate?: number;
}

interface RemainingTermLengthInterface {
  start: Date;
  end: string;
}

/**
 * Returns the next due date, formatted as a string.
 * @param firstDueDate string (Required)
 */
export const getNextDueDate = ({
  firstDueDate,
  today = new Date(),
  delay = 0,
  monthlyPaymentDate,
}: NextDueDateInterface): string => {
  if (isBefore(today, firstDueDate)) {
    const nextDueDateWithDelay = addMonths(firstDueDate, delay);
    return formatDate(nextDueDateWithDelay, 'MMMM d, yyyy');
  }
  const monthsPast = differenceInMonths(today, firstDueDate);
  const nextDueDate = addMonths(firstDueDate, monthsPast + 1);
  const nextDueDateWithDelay = addMonths(nextDueDate, delay);
  if (monthlyPaymentDate) {
    const lastDayOfMonth = endOfMonth(nextDueDateWithDelay);
    const maxDayOfMonth = lastDayOfMonth.getDate();

    if (monthlyPaymentDate > maxDayOfMonth) {
      // Adjust monthlyPaymentDate to the maximum day of the month
      monthlyPaymentDate = maxDayOfMonth;
    }

    nextDueDateWithDelay.setDate(monthlyPaymentDate);
  }

  return formatDate(nextDueDateWithDelay, 'MMMM d, yyyy');
};

/**
 * Returns the remaining term length, in months.
 * @param endDateForPayments string (Required)
 */
export const getRemainingTermLengthInMonths = ({ start = new Date(), end }: RemainingTermLengthInterface): number => {
  if (isAfter(start, end)) {
    return 0;
  }
  let termLength = 0;
  for (let i = 1; i <= 12; i++) {
    const nextDate = addMonths(start, i);
    termLength++;
    if (isAfter(nextDate, end)) {
      break;
    }
  }
  return termLength;
};
